import { RefObject, useEffect } from 'react'
import { ChartAPI } from 'c3'

/*
 * Keeps a c3 chart sized to its container as the container changes width, one redraw per animation
 * frame and without c3's redraw transition. Charts using this pass `resize: { auto: false }` so c3
 * doesn't also redraw (animated, after a delay) on window resize events: that delayed, animated
 * redraw is what made charts jump about while the brief's layout was animating.
 */
export const useChartResize = (
  elementRef: RefObject<HTMLElement>,
  chartRef: RefObject<ChartAPI | null>,
  enabled = true
): void => {
  useEffect(() => {
    const el = elementRef.current
    if (!enabled || !el || typeof ResizeObserver === 'undefined') return undefined

    let frame = 0
    let lastWidth = el.clientWidth
    const observer = new ResizeObserver(() => {
      if (frame) return
      frame = window.requestAnimationFrame(() => {
        frame = 0
        const width = el.clientWidth
        if (width === lastWidth || width === 0) return
        lastWidth = width
        chartRef.current?.resize({ width, height: el.clientHeight })
      })
    })
    observer.observe(el)
    return () => {
      observer.disconnect()
      if (frame) window.cancelAnimationFrame(frame)
    }
  }, [elementRef, chartRef, enabled])
}
